import React from "react";
import { NavLink, withRouter } from "react-router-dom";
import PortfolioSidebar from "containers/PortfolioSidebar";
import moment from "moment";
import PropTypes from "prop-types";
import Dashboard from "rialto-icons/Dashboard";
import Datepicker from "rialto-icons/DatepickerSmall";
import Information from "rialto-icons/Information";
import Settings from "rialto-icons/Settings";
import Signout from "rialto-icons/Signout";
import { currentUserShape, portfolioShape } from "shapes";

import Dropdown, { DropdownButton } from "components/Dropdown";
import ScreenWidth from "components/hoc/ScreenWidth";
import UserDetails from "components/UserDetails";
import Translate from "utils/translate";
import { createUrlParams, getUrlParams } from "utils/urlParams";

import BackButton from "../BackButton";
import NavbarBody from "../NavbarBody/NavbarBody";
import PortfolioToggle from "../PortfolioToggle";

import styles from "./Navbar.css";

const { t } = new Translate("Navbar");

const Navbar = (props) => {
  const {
    currentAsset,
    currentAssetLead,
    currentDeal,
    currentUser,
    hasBackButton,
    history,
    isAssetDetail,
    isAssetLeadDetail,
    isCalendar,
    isDashboard,
    isDealDetail,
    isMobile,
    isSettings,
    onBackClick,
    onLogoutClick,
    onOpenSidebar,
    onSettingsClick,
    onToggleIntercom,
    portfolios,
    selectedPortfolioIDs,
  } = props;

  const Input = () => (
    <DropdownButton>
      <UserDetails isTextHidden={isMobile} title={currentUser.company.name} user={currentUser} />
    </DropdownButton>
  );

  const handleBack = () => {
    const dealsUrl = `/deals${createUrlParams({
      portfolioIDs: selectedPortfolioIDs,
    })}`;

    if (history.length > 2) return onBackClick();
    if (isDealDetail) return history.push(dealsUrl);
    if (isAssetDetail) return history.push(`/deals?portfolioIDs[]=${currentAsset.portfolio.id}`);

    return history.push("/");
  };

  const {
    company: { dealDashboardUrl, marketingDashboardUrl },
  } = currentUser;
  const { type } = getUrlParams();
  const openDashboard = (d) => history.push(`/dashboard?type=${d}`);
  const DashboardLink = () =>
    !marketingDashboardUrl || !dealDashboardUrl ? (
      <NavLink to="/dashboard">
        <Dashboard />
      </NavLink>
    ) : (
      <Dropdown
        inputComponent={() => <Dashboard />}
        onChange={openDashboard}
        selectedValues={type ? [type] : []}
        valueExtractor={(d) => ({ label: t(`dashboard.${d}`) })}
        values={["marketing", "deal"]}
      />
    );

  return (
    <>
      <nav className={styles.wrapper}>
        <div className={styles.dateNow}>{moment().format("L")}</div>
        {hasBackButton ? (
          <BackButton onBack={handleBack} />
        ) : (
          <PortfolioToggle onClick={onOpenSidebar} />
        )}
        <div className={styles.navbarBody}>
          <NavbarBody
            currentAsset={currentAsset}
            currentAssetLead={currentAssetLead}
            currentDeal={currentDeal}
            isAssetDetail={isAssetDetail}
            isAssetLeadDetail={isAssetLeadDetail}
            isCalendar={isCalendar}
            isDashboard={isDashboard}
            isDealDetail={isDealDetail}
            isSettings={isSettings}
            portfolios={portfolios}
            selectedPortfolioIDs={selectedPortfolioIDs}
          />
        </div>
        <div className={styles.right}>
          {!isMobile && (
            <>
              {(dealDashboardUrl || marketingDashboardUrl) && <DashboardLink />}
              <NavLink to="/calendar">
                <Datepicker />
              </NavLink>
              <div className={styles.divider} />
            </>
          )}
          <Dropdown
            inputComponent={Input}
            onChange={(item) => item.onClick()}
            values={[
              {
                icon: Settings,
                label: t("settings"),
                onClick: onSettingsClick,
              },
              ...(isMobile && (dealDashboardUrl || marketingDashboardUrl)
                ? [
                    ...(dealDashboardUrl
                      ? [
                          {
                            icon: Dashboard,
                            label: t("dashboard.deal"),
                            onClick: () => openDashboard("deal"),
                          },
                        ]
                      : []),
                    ...(marketingDashboardUrl
                      ? [
                          {
                            icon: Dashboard,
                            label: t("dashboard.marketing"),
                            onClick: () => openDashboard("marketing"),
                          },
                        ]
                      : []),
                  ]
                : []),
              {
                icon: Information,
                label: t("help"),
                onClick: onToggleIntercom,
              },
              { icon: Signout, label: t("sign_out"), onClick: onLogoutClick },
            ]}
            expandValues
          />
        </div>
      </nav>
      <PortfolioSidebar />
    </>
  );
};

Navbar.propTypes = {
  currentAsset: PropTypes.shape({
    name: PropTypes.string.isRequired,
    portfolio: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
    }),
  }),
  currentAssetLead: PropTypes.shape({
    name: PropTypes.string.isRequired,
    portfolio: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  currentDeal: PropTypes.shape({
    name: PropTypes.string.isRequired,
    portfolio: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  selectedPortfolioIDs: PropTypes.arrayOf(PropTypes.number),
  currentUser: PropTypes.shape(currentUserShape).isRequired,
  history: PropTypes.shape({
    length: PropTypes.number,
    push: PropTypes.func,
  }),
  hasBackButton: PropTypes.bool,
  portfolios: PropTypes.arrayOf(PropTypes.shape(portfolioShape)),
  isAssetDetail: PropTypes.bool,
  isAssetLeadDetail: PropTypes.bool,
  isCalendar: PropTypes.bool,
  isDealDetail: PropTypes.bool,
  isDashboard: PropTypes.bool,
  isMobile: PropTypes.bool,
  isSettings: PropTypes.bool,
  onBackClick: PropTypes.func.isRequired,
  onLogoutClick: PropTypes.func.isRequired,
  onOpenSidebar: PropTypes.func.isRequired,
  onSettingsClick: PropTypes.func,
  onToggleIntercom: PropTypes.func.isRequired,
};

export default withRouter(ScreenWidth(Navbar));
